/**
 * K|KASA Wishlist — localStorage-powered, no backend required.
 * Reads/writes to 'kk-wishlist' in localStorage.
 * Injects heart buttons into product cards and syncs the drawer.
 *
 * v2 — Fixes:
 *  · MutationObserver re-entrancy guard (was causing infinite loop /
 *    Shopify Theme Editor crash and Chrome preview stall)
 *  · Debounced observer callback (100 ms) so rapid DOM changes batch
 *  · Observer disconnects before DOM writes, reconnects after
 *  · Shopify design-mode check skips observer entirely in customizer
 *  · Hover-swap observer given same treatment
 */

(function () {
  'use strict';

  /* ── Storage ─────────────────────────────────────────────── */
  var STORAGE_KEY = 'kk-wishlist';

  function getItems() {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    } catch (e) {
      return [];
    }
  }

  function saveItems(items) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }

  function hasItem(handle) {
    return getItems().some(function (i) { return i.handle === handle; });
  }

  function toggleItem(item) {
    var items = getItems();
    var idx = items.findIndex(function (i) { return i.handle === item.handle; });
    if (idx > -1) {
      items.splice(idx, 1);
    } else {
      items.push(item);
    }
    saveItems(items);
    return idx === -1; // true = was added
  }

  function removeItem(handle) {
    saveItems(getItems().filter(function (i) { return i.handle !== handle; }));
  }

  /* ── Count badge ─────────────────────────────────────────── */
  function updateBadge() {
    var count = getItems().length;
    document.querySelectorAll('[data-kk-wishlist-count]').forEach(function (el) {
      el.textContent = count;
      if (el.classList.contains('kk-header__badge')) {
        el.style.display = count > 0 ? '' : 'none';
      } else {
        el.style.display = 'inline';
      }
    });
    document.querySelectorAll('[data-kk-wishlist-trigger]').forEach(function (el) {
      el.setAttribute('aria-label', 'Wishlist, ' + count + ' item' + (count !== 1 ? 's' : ''));
    });
  }

  /* ── Heart button factory ────────────────────────────────── */
  function createHeart(handle, title, image, price) {
    var btn = document.createElement('button');
    btn.className = 'kk-heart-btn';
    btn.setAttribute('type', 'button');
    btn.setAttribute('data-kk-heart', handle);
    btn.setAttribute('aria-label', 'Save ' + title + ' to wishlist');
    btn.innerHTML = heartSVG();

    btn.classList.toggle('is-active', hasItem(handle));

    btn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      var wasAdded = toggleItem({ handle: handle, title: title, image: image, price: price });
      // Sync all hearts for this product — pause observer so classList change
      // doesn't re-trigger injection
      pauseObserver();
      document.querySelectorAll('[data-kk-heart="' + handle + '"]').forEach(function (h) {
        h.classList.toggle('is-active', wasAdded);
      });
      resumeObserver();
      updateBadge();
      renderDrawer();
      // Micro-animation
      btn.classList.add('kk-heart-btn--pop');
      setTimeout(function () { btn.classList.remove('kk-heart-btn--pop'); }, 350);
    });

    return btn;
  }

  function heartSVG() {
    return '<svg class="kk-heart-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>';
  }

  /* ── Inject hearts into product cards ───────────────────── */
  function injectHeartsIntoGrid() {
    
    // 1. Restore active state on ANY manually authored hearts that Shopify just injected via AJAX
    document.querySelectorAll('[data-kk-heart]:not(.is-active)').forEach(function (btn) {
      if (hasItem(btn.getAttribute('data-kk-heart'))) {
        btn.classList.add('is-active');
      }
    });

    // 2. Inject hearts into our KK product grid cards
    document.querySelectorAll('.kk-pgrid__card').forEach(function (card) {
      if (card.querySelector('[data-kk-heart]')) return;
      var handle = card.getAttribute('href') && card.getAttribute('href').split('/products/')[1];
      if (!handle) return;
      var title = card.querySelector('.kk-pgrid__title');
      var image = card.querySelector('.kk-pgrid__img');
      var price = card.querySelector('.kk-pgrid__price');
      var heart = createHeart(
        handle,
        title ? title.textContent.trim() : handle,
        image ? image.src : null,
        price ? price.textContent.trim() : ''
      );
      var mediaEl = card.querySelector('.kk-pgrid__media');
      if (mediaEl) {
        pauseObserver();
        mediaEl.appendChild(heart);
        resumeObserver();
      }
    });

    // Theme native product cards (collection pages, recommendations)
    document.querySelectorAll('product-card, .product-card').forEach(function (card) {
      if (card.querySelector('[data-kk-heart]')) return;
      var link = card.querySelector('a[href*="/products/"]');
      if (!link) return;
      var parts = link.getAttribute('href').split('/products/');
      if (parts.length < 2) return;
      var handle = parts[1].split('?')[0].split('#')[0];
      if (!handle) return;
      var title = card.querySelector('product-title, .product-title, h3, h4');
      var image = card.querySelector('img');
      var price = card.querySelector('.price, [data-price]');
      var heart = createHeart(
        handle,
        title ? title.textContent.trim() : handle,
        image ? image.src : null,
        price ? price.textContent.trim() : ''
      );
      heart.classList.add('kk-heart-btn--card');
      var media = card.querySelector('.media, .product-card__media, figure, .kk-cat-tile__media');
      pauseObserver();
      if (media) {
        media.style.position = 'relative';
        media.appendChild(heart);
      } else {
        card.insertBefore(heart, card.firstChild);
      }
      resumeObserver();
    });
  }

  /* ── Drawer HTML ─────────────────────────────────────────── */
  function renderDrawer() {
    var drawer = document.getElementById('kk-wishlist-drawer');
    if (!drawer) return;
    var items = getItems();
    var countEl = drawer.querySelector('[data-kk-drawer-count]');
    if (countEl) {
      countEl.textContent = items.length === 0
        ? 'Empty'
        : items.length + ' saved piece' + (items.length !== 1 ? 's' : '');
    }
    var body = drawer.querySelector('[data-kk-drawer-body]');
    if (!body) return;

    if (items.length === 0) {
      body.innerHTML = '<div class="kk-wishlist-empty">' +
        '<svg class="kk-wishlist-empty__icon" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" aria-hidden="true"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>' +
        '<p class="kk-wishlist-empty__title">No saved pieces yet</p>' +
        '<p class="kk-wishlist-empty__body">Tap the heart on any piece you love to save it here.</p>' +
        '<a href="/collections/all" class="kk-wishlist-empty__cta" data-kk-drawer-close>Browse the collection</a>' +
        '</div>';
    } else {
      body.innerHTML = '<ul class="kk-wishlist-list">' +
        items.map(function (item) {
          return '<li class="kk-wishlist-item">' +
            '<a href="/products/' + item.handle + '" class="kk-wishlist-item__img-wrap" data-kk-drawer-close>' +
              (item.image ? '<img src="' + item.image + '" alt="' + escapeHtml(item.title) + '" class="kk-wishlist-item__img">' : '<div class="kk-wishlist-item__img-placeholder"></div>') +
            '</a>' +
            '<div class="kk-wishlist-item__details">' +
              '<div class="kk-wishlist-item__row">' +
                '<a href="/products/' + item.handle + '" class="kk-wishlist-item__title" data-kk-drawer-close>' + escapeHtml(item.title) + '</a>' +
                '<span class="kk-wishlist-item__price">' + escapeHtml(item.price) + '</span>' +
              '</div>' +
              '<div class="kk-wishlist-item__actions">' +
                '<a href="/products/' + item.handle + '" class="kk-wishlist-item__shop" data-kk-drawer-close>Shop now</a>' +
                '<button type="button" class="kk-wishlist-item__remove" data-kk-remove="' + escapeHtml(item.handle) + '">Remove</button>' +
              '</div>' +
            '</div>' +
            '</li>';
        }).join('') +
        '</ul>';

      body.querySelectorAll('[data-kk-remove]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var handle = btn.getAttribute('data-kk-remove');
          removeItem(handle);
          pauseObserver();
          document.querySelectorAll('[data-kk-heart="' + handle + '"]').forEach(function (h) {
            h.classList.remove('is-active');
          });
          resumeObserver();
          updateBadge();
          renderDrawer();
        });
      });
    }

    body.querySelectorAll('[data-kk-drawer-close]').forEach(function (el) {
      el.addEventListener('click', closeDrawer);
    });
  }

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /* ── Drawer open / close ─────────────────────────────────── */
  function openDrawer() {
    var drawer = document.getElementById('kk-wishlist-drawer');
    var overlay = document.getElementById('kk-wishlist-overlay');
    if (!drawer) return;
    renderDrawer();
    drawer.classList.add('is-open');
    if (overlay) overlay.classList.add('is-open');
    document.body.classList.add('kk-drawer-lock');
  }

  function closeDrawer() {
    var drawer = document.getElementById('kk-wishlist-drawer');
    var overlay = document.getElementById('kk-wishlist-overlay');
    if (!drawer) return;
    drawer.classList.remove('is-open');
    if (overlay) overlay.classList.remove('is-open');
    document.body.classList.remove('kk-drawer-lock');
  }

  // Expose globally on window
  window.kkOpenWishlist = openDrawer;
  window.kkCloseWishlist = closeDrawer;

  /* ── Safe MutationObserver with debounce + re-entrancy guard ── */
  var _observer = null;
  var _observerActive = false;
  var _debounceTimer = null;
  var _isInjecting = false;

  function pauseObserver() {
    if (_observer && _observerActive) {
      _observer.disconnect();
      _observerActive = false;
    }
  }

  function resumeObserver() {
    if (_observer && !_observerActive) {
      _observer.observe(document.body, { childList: true, subtree: true });
      _observerActive = true;
    }
  }

  function createObserver() {
    // Never run the observer inside Shopify's Theme Editor — it re-renders
    // sections constantly and the DOM noise causes an infinite loop crash.
    if (document.documentElement.classList.contains('shopify-design-mode')) return;

    if (typeof MutationObserver === 'undefined') return;

    _observer = new MutationObserver(function (mutations) {
      // Skip if we're already in the middle of an injection pass
      if (_isInjecting) return;

      // Only act on mutations that added real element nodes (not text/attr changes)
      var hasAddedNodes = mutations.some(function (m) {
        return m.addedNodes.length > 0 &&
          Array.from(m.addedNodes).some(function (n) { return n.nodeType === 1; });
      });
      if (!hasAddedNodes) return;

      // Debounce so rapid mutations (e.g. filter reloads) batch into one pass
      clearTimeout(_debounceTimer);
      _debounceTimer = setTimeout(function () {
        _isInjecting = true;
        pauseObserver();        // disconnect before touching DOM
        try {
          injectHeartsIntoGrid();
          updateBadge();
        } finally {
          _isInjecting = false;
          resumeObserver();     // reconnect after DOM writes are done
        }
      }, 100);
    });

    _observer.observe(document.body, { childList: true, subtree: true });
    _observerActive = true;
  }

  /* ── Bootstrap ───────────────────────────────────────────── */
  function init() {
    updateBadge();
    injectHeartsIntoGrid();
    renderDrawer();

    document.querySelectorAll('[data-kk-wishlist-trigger]').forEach(function (btn) {
      btn.addEventListener('click', openDrawer);
    });

    var overlay = document.getElementById('kk-wishlist-overlay');
    if (overlay) overlay.addEventListener('click', closeDrawer);

    document.querySelectorAll('[data-kk-drawer-close-btn]').forEach(function (btn) {
      btn.addEventListener('click', closeDrawer);
    });

    createObserver();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Support Shopify Theme Editor re-renders
  document.addEventListener('shopify:section:load', function() {
    injectHeartsIntoGrid();
    updateBadge();
  });

  // Real-time synchronization across browser tabs and windows
  window.addEventListener('storage', function(e) {
    if (e.key === STORAGE_KEY) {
      updateBadge();
      var drawer = document.getElementById('kk-wishlist-drawer');
      if (drawer && drawer.classList.contains('is-open')) {
        renderDrawer();
      }
    }
  });

  /* ── Global Event Delegation for Wishlist Triggers & Hearts ──────────── */
  document.addEventListener('click', function(e) {
    var trigger = e.target.closest('[data-kk-wishlist-trigger]');
    if (trigger) {
      e.preventDefault();
      openDrawer();
      return;
    }

    if (e.target.closest('[data-kk-drawer-close-btn], #kk-wishlist-overlay')) {
      e.preventDefault();
      closeDrawer();
      return;
    }

    var btn = e.target.closest('[data-kk-heart]');
    if (!btn) return;
    
    // If it doesn't have the title attribute, it was generated by our observer which already bound a listener
    if (!btn.hasAttribute('data-kk-heart-title')) return;

    e.preventDefault();
    e.stopPropagation();

    var handle = btn.getAttribute('data-kk-heart');
    var title = btn.getAttribute('data-kk-heart-title') || handle;
    var image = btn.getAttribute('data-kk-heart-image') || '';
    var price = btn.getAttribute('data-kk-heart-price') || '';

    var wasAdded = toggleItem({ handle: handle, title: title, image: image, price: price });
    
    pauseObserver();
    document.querySelectorAll('[data-kk-heart="' + handle + '"]').forEach(function (h) {
      h.classList.toggle('is-active', wasAdded);
    });
    resumeObserver();
    updateBadge();
    renderDrawer();

    btn.classList.add('kk-heart-btn--pop');
    setTimeout(function () { btn.classList.remove('kk-heart-btn--pop'); }, 350);
  });

  /* ── Public API ──────────────────────────────────────────── */
  window.kkWishlist = {
    toggle: function (handle, title, image, price) {
      var wasAdded = toggleItem({ handle: handle, title: title, image: image, price: price });
      pauseObserver();
      document.querySelectorAll('[data-kk-heart="' + handle + '"]').forEach(function (h) {
        h.classList.toggle('is-active', wasAdded);
      });
      resumeObserver();
      updateBadge();
      renderDrawer();
      return wasAdded;
    },
    has: hasItem,
    openDrawer: openDrawer,
    closeDrawer: closeDrawer
  };

})();

/* ── Hover Image Swap ─────────────────────────────────────────
 * Fades second product image in on card hover.
 * Uses the same debounce + guard pattern to avoid observer loops.
 * ─────────────────────────────────────────────────────────── */
(function () {
  'use strict';

  // Skip entirely inside Shopify Theme Editor
  if (document.documentElement.classList.contains('shopify-design-mode')) return;

  var _swapObserver = null;
  var _swapTimer = null;
  var _swapBusy = false;

  function initHoverSwap() {
    var galleries = document.querySelectorAll('product-card-gallery, .card__media');
    galleries.forEach(function (gallery) {
      if (gallery.dataset.kkSwapInit) return;
      gallery.dataset.kkSwapInit = '1';

      var imgs = gallery.querySelectorAll('img');
      if (imgs.length < 2) return;

      var second = imgs[1];
      second.setAttribute('data-kk-alt-image', '1');

      // Apply styles without triggering observer (observer is paused during init)
      second.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;object-fit:cover;opacity:0;transition:opacity 0.5s ease;z-index:1;';

      gallery.style.position = 'relative';
      gallery.style.overflow = 'hidden';

      var card = gallery.closest('product-card, .product-card, .kk-pgrid__card, .kk-recs__card');
      if (!card) return;

      card.addEventListener('mouseenter', function () { second.style.opacity = '1'; });
      card.addEventListener('mouseleave', function () { second.style.opacity = '0'; });
    });
  }

  function runSwap() {
    if (_swapBusy) return;
    _swapBusy = true;
    if (_swapObserver) _swapObserver.disconnect();
    try {
      initHoverSwap();
    } finally {
      _swapBusy = false;
      if (_swapObserver) {
        _swapObserver.observe(document.body, { childList: true, subtree: true });
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runSwap);
  } else {
    runSwap();
  }

  // Support Shopify Theme Editor re-renders
  document.addEventListener('shopify:section:load', runSwap);

  if (typeof MutationObserver !== 'undefined') {
    _swapObserver = new MutationObserver(function (mutations) {
      if (_swapBusy) return;
      var hasElements = mutations.some(function (m) {
        return m.addedNodes.length > 0 &&
          Array.from(m.addedNodes).some(function (n) { return n.nodeType === 1; });
      });
      if (!hasElements) return;
      clearTimeout(_swapTimer);
      _swapTimer = setTimeout(runSwap, 120);
    });
    _swapObserver.observe(document.body, { childList: true, subtree: true });
  }

})();
